/**
 * What a bag holds at the start, edited on the bag during a script preview:
 * which fluid (or empty), how much, the gas a bag holds above it, and
 * whether it is where fluid comes in or goes out. Volumes are typed in the design's volume unit (D49) and
 * stored in m³ (invariant 5).
 */
import { useEffect, useId, useRef, useState } from "react";
import { useLiquidUnits } from "../workspace/DisplayUnitsContext";
import { useKeepInside } from "./keepInside";
import { formatNumber, fromCanonical, toCanonical, typedNumber } from "../workspace/units";

export interface BagValue {
  /** A fluid's name, "air" for empty, or a new fluid's description in `newFluid`. */
  fluid: string;
  newFluid?: string;
  /** The new fluid's colour, as #rrggbb. */
  newColour?: string;
  volume: number | null;
  /** A bag's gas above its liquid (m³): run dry, it follows the liquid out. */
  headspace: number | null;
  role: "" | "input" | "output";
}

const NEW = "__new__";

export function BagEditor({
  name,
  fluids,
  initial,
  nextColour = "#3f8fd2",
  collapsible = false,
  x,
  y,
  onCommit,
  onCancel,
}: {
  name: string;
  fluids: readonly { name: string; label: string }[];
  /** The colour a new fluid starts with: the one it would take anyway. */
  nextColour?: string;
  initial: BagValue;
  /** A bag that collapses as it empties: only it can hold gas above its liquid worth stating. */
  collapsible?: boolean;
  /** Screen position of the bag's centre. */
  x: number;
  y: number;
  onCommit: (value: BagValue) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const unit = useLiquidUnits().volume;
  const [fluid, setFluid] = useState(fluids.length === 0 && initial.fluid === "air" ? NEW : initial.fluid);
  const [newFluid, setNewFluid] = useState("");
  const [newColour, setNewColour] = useState(nextColour);
  const [volume, setVolume] = useState(initial.volume === null ? "" : formatNumber(fromCanonical(initial.volume, unit)));
  const [headspace, setHeadspace] = useState(initial.headspace === null ? "" : formatNumber(fromCanonical(initial.headspace, unit)));
  const [role, setRole] = useState(initial.role);
  const formRef = useRef<HTMLFormElement>(null);
  useKeepInside(formRef);
  const firstRef = useRef<HTMLSelectElement>(null);
  useEffect(() => firstRef.current?.focus(), []);

  const parsed = volume.trim() === "" ? null : typedNumber(volume);
  const gas = headspace.trim() === "" ? null : typedNumber(headspace);
  const amount = (value: number | null) => value === null || (Number.isFinite(value) && value >= 0);
  const valid = amount(parsed) && amount(gas) && (fluid !== NEW || newFluid.trim() !== "");

  return (
    <form
      ref={formRef}
      data-label-editor=""
      className="cx-port-editor cx-bag-editor"
      aria-label={`Contents of ${name}`}
      style={{ left: x, top: y }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onCommit({
          fluid: fluid === NEW ? "" : fluid,
          ...(fluid === NEW ? { newFluid: newFluid.trim(), newColour } : {}),
          volume: parsed === null ? null : toCanonical(parsed, unit),
          headspace: collapsible && gas !== null && gas > 0 ? toCanonical(gas, unit) : null,
          role,
        });
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
      onBlur={(event) => {
        if (!formRef.current?.contains(event.relatedTarget as Node | null)) onCancel();
      }}
    >
      <label htmlFor={`${id}-fluid`}>Holds</label>
      <select id={`${id}-fluid`} ref={firstRef} value={fluid} onChange={(event) => setFluid(event.target.value)}>
        <option value="air">empty (air)</option>
        {fluids.map((option) => (
          <option key={option.name} value={option.name}>
            {option.label}
          </option>
        ))}
        <option value={NEW}>New fluid…</option>
      </select>
      {fluid === NEW && (
        <>
          <label htmlFor={`${id}-new`} className="visually-hidden">
            New fluid
          </label>
          <input
            id={`${id}-new`}
            className="cx-bag-new"
            value={newFluid}
            placeholder="e.g. Bulk IVT"
            onChange={(event) => setNewFluid(event.target.value)}
          />
          <input
            type="color"
            className="sx-swatch"
            aria-label="New fluid's colour"
            title="Colour on the drawing"
            value={newColour}
            onChange={(event) => setNewColour(event.target.value)}
          />
        </>
      )}
      <label htmlFor={`${id}-volume`}>Volume</label>
      <input
        id={`${id}-volume`}
        inputMode="decimal"
        value={volume}
        aria-invalid={amount(parsed) ? undefined : true}
        onChange={(event) => setVolume(event.target.value)}
      />
      <span>{unit.label}</span>
      {collapsible && (
        <>
          <label htmlFor={`${id}-headspace`} title="Gas in the bag above its liquid: run dry, it follows the liquid out and clears the lines behind it">
            Gas above
          </label>
          <input
            id={`${id}-headspace`}
            inputMode="decimal"
            value={headspace}
            placeholder="0"
            aria-invalid={amount(gas) ? undefined : true}
            onChange={(event) => setHeadspace(event.target.value)}
          />
          <span>{unit.label}</span>
        </>
      )}
      <label htmlFor={`${id}-role`} className="visually-hidden">
        Input or output
      </label>
      <select id={`${id}-role`} value={role} onChange={(event) => setRole(event.target.value as BagValue["role"])}>
        <option value="">—</option>
        <option value="input">input</option>
        <option value="output">output</option>
      </select>
      {/* As in a port's editor: the press must not move focus, or WebKit — which gives a clicked button none — blurs this form out of existence before the click lands. */}
      <button type="submit" disabled={!valid} onMouseDown={(event) => event.preventDefault()}>
        Set
      </button>
    </form>
  );
}
